import React from "react";
import { AiFillStar } from "react-icons/ai";
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegCalendarAlt,
  FaRegClock,
  FaUserAlt,
} from "react-icons/fa";

import { FaMoneyBillWave, FaCreditCard, FaGlobe } from "react-icons/fa";

// Format total mins → "1 hr 30 mins"
const formatDuration = (mins) => {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  if (hours && minutes) return `${hours} hr ${minutes} mins`;
  if (hours) return `${hours} hr`;
  return `${minutes} mins`;
};

// Extract mins from desc like "30 mins", "1 hr", "1 hr 30 mins"
const extractMinutes = (desc) => {
  const lower = desc?.toLowerCase() || "";
  const hrMatch = lower.match(/(\d+)\s*hr/);
  const minMatch = lower.match(/(\d+)\s*min/);

  const hr = hrMatch ? parseInt(hrMatch[1], 10) : 0;
  const min = minMatch ? parseInt(minMatch[1], 10) : 0;

  return hr * 60 + min;
};

const SummaryCard = ({
  name,
  email,
  mobile,
  selectedServices,
  selectedProfessionalId,
  staffs,
  selectedDateTime,
  message,
  paymentMethod, // ✅ Add this

  store,
}) => {
  console.log({ store });

  const paymentOptions = [
    {
      value: "cash",
      label: "Cash at Kiosk",
      sub: "Pay with cash at pickup",
      icon: <FaMoneyBillWave className="text-redlight text-xl" />,
    },
    {
      value: "card",
      label: "Card at Kiosk",
      sub: "Pay with card at pickup counter",
      icon: <FaCreditCard className="text-redlight text-xl" />,
    },
    {
      value: "online",
      label: "Online Payment",
      sub: "Pay securely online via Stripe",
      icon: <FaGlobe className="text-redlight text-xl" />,
    },
  ];

  const selectedPayment = paymentOptions.find(
    (option) => option.value === paymentMethod
  );

  const renderServiceListWithTime = (services) => {
    if (!services.length)
      return <p className="text-base text-gray-400 italic">None selected</p>;

    const totalMins = services.reduce(
      (acc, s) => acc + extractMinutes(s.desc),
      0
    );

    const totalPrice = services.reduce(
      (sum, s) => sum + Number(s.price || 0),
      0
    );
    const gst = ((totalPrice * 3) / 23).toFixed(2); // ~15%

    return (
      <>
        {services.map((item, i) => (
          <div key={i} className="flex justify-between items-start mb-3">
            <div className="flex items-start space-x-2">
              <div className="w-6 h-6 bg-redlight rounded-md flex items-center justify-center mt-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-reddark"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <p className="text-md font-semibold text-white">{item.title}</p>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            </div>
            <p className="text-sm font-semibold text-text whitespace-nowrap">
              ${Number(item.price).toFixed(2)}
            </p>
          </div>
        ))}

        <div className="text-sm text-gray-300 space-y-1">
          {" "}
          <div className="flex justify-start">
            <span>Total Duration : &nbsp; </span>
            <span className="font-semibold text-redlight">
              {formatDuration(totalMins)}
            </span>
          </div>{" "}
        </div>
        <hr className="border-border my-4" />

        <div className="text-sm text-gray-300 space-y-1">
          <div className="flex justify-between">
            <span>GST (15% included)</span>
            <span>${gst}</span>
          </div>
          <div className="flex justify-between text-white font-semibold text-base">
            <span>Total</span>
            <span>${totalPrice.toFixed(2)}</span>
          </div>
        </div>
      </>
    );
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;

    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const totalDurationMins = selectedServices.reduce(
    (acc, s) => acc + extractMinutes(s.desc),
    0
  );

  //

  const formatDateRange = (startStr, totalMins) => {
    const [datePart, timePart] = startStr.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    let [hour, minute] = timePart.split(":").map(Number);
    const isPM = startStr.toLowerCase().includes("pm");

    if (isPM && hour < 12) hour += 12;
    if (!isPM && hour === 12) hour = 0;

    const start = new Date(year, month - 1, day, hour, minute);
    const end = new Date(start.getTime() + totalMins * 60000);

    const dateOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const timeOptions = { hour: "numeric", minute: "2-digit", hour12: true };

    const datePartFormatted = start.toLocaleDateString("en-US", dateOptions);
    const startTime = start.toLocaleTimeString("en-US", timeOptions);
    const endTime = end.toLocaleTimeString("en-US", timeOptions);

    return {
      datePart: datePartFormatted,
      timeRange: `${startTime} – ${endTime}`,
    };
  };

  const SectionCard = ({ title, children }) => (
    <div className="bg-white/10 p-4 rounded-2xl shadow border border-border">
      <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
        {title}
      </h4>
      <div>{children}</div>
    </div>
  );

  return (
    <div className="space-y-6  shadow-lg bg-dry dark:bg-neutral-900">
      <h2 className="text-xl font-bold text-redlight  border-b border-border pb-4">
        Booking Summary
      </h2>
      {/*  Services with Total Duration */}
      <SectionCard title="Services">
        {renderServiceListWithTime(selectedServices)}
      </SectionCard>

      {/* Preferred Professional */}
      <SectionCard title="Preferred Professional">
        {selectedProfessionalId?._id === "any" ? (
          <p className="text-base text-gray-400 italic">Any available</p>
        ) : selectedProfessionalId?._id === "per-service" ? (
          <p className="text-base text-gray-400 italic">Selected per service</p>
        ) : (
          (() => {
            const staff = staffs.find(
              (s) => s._id === selectedProfessionalId?._id
            );
            return staff ? (
              <div className="flex items-center space-x-3 mt-2">
                <img
                  src={staff.photo}
                  alt={staff.name}
                  className="w-6 h-6 rounded-full object-cover "
                />
                <div className="flex flex-col">
                  <p className="text-md font-semibold text-white">
                    {staff.name}
                  </p>
                  {staff.role && (
                    <p className="text-sm text-gray-400">{staff.role}</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-base text-gray-400 italic">Unknown staff</p>
            );
          })()
        )}
      </SectionCard>
      {/* Appointment Time */}
      <SectionCard title="Appointment Time">
        {selectedDateTime ? (
          (() => {
            const { datePart, timeRange } = formatDateRange(
              selectedDateTime,
              totalDurationMins
            );

            return (
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-4">
                  <FaRegCalendarAlt className="text-white" />
                  <p className="text-white font-semibold text-sm">{datePart}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <FaRegClock className="text-white" />
                  <p className="text-redlight font-semibold text-sm">
                    {timeRange}
                  </p>
                </div>
              </div>
            );
          })()
        ) : (
          <p className="text-base text-gray-400 italic">Not selected</p>
        )}
      </SectionCard>
      {/* Payment Method */}
      <SectionCard title="Payment Method">
        {selectedPayment ? (
          <div className="flex items-start space-x-3">
            <div className="pt-1">{selectedPayment.icon}</div>
            <div className="flex flex-col">
              <p className="text-md font-semibold text-white">
                {selectedPayment.label}
              </p>
              <p className="text-sm text-gray-400">{selectedPayment.sub}</p>
            </div>
          </div>
        ) : (
          <p className="text-base text-gray-400 italic">Not selected</p>
        )}
      </SectionCard>

      {/* Contact Info */}
      <SectionCard title="Your Contact Info">
        <ul className="space-y-3">
          <li className="flex items-start space-x-3">
            <FaUserAlt className="text-white mt-1" />
            <div>
              <p className="text-sm text-gray-400">
                {" "}
                <span className="text-md font-semibold text-white uppercase">
                  {name || "—"}
                </span>
              </p>
              <p className="text-sm text-gray-400">
                {" "}
                <span className="text-sm  text-textGray">{email || "—"}</span>
              </p>
              <p className="text-sm text-gray-400">
                <span className="text-sm  text-textGray">{mobile || "—"}</span>
              </p>
            </div>
          </li>
        </ul>
      </SectionCard>
      {/* Store location */}
      <SectionCard title="Location">
        <div className="flex items-start space-x-4">
          <img
            src={store?.photo}
            alt={store?.name}
            className="w-12 h-12 rounded-full object-cover "
          />
          <div className="flex flex-col">
            <p className="text-lg font-semibold text-white uppercase">
              {store?.name || "Unknown"}
            </p>
            <p className="text-sm text-textGray">
              {store?.address || "No address"}
            </p>
            <p className="text-sm text-textGray">
              {store?.phone || "No phone number"}
            </p>
          </div>
        </div>
      </SectionCard>
      {/* Additional Notes */}
      <SectionCard title="Additional Notes">
        <p className="text-lg font-semibold text-white">{message || "—"}</p>
      </SectionCard>
    </div>
  );
};

export default SummaryCard;
